import type { HTMLAttributes, ReactNode } from 'react'
import { Link } from '@inertiajs/react'
import styles from './Typography.module.css'

export type TextTone = 'default' | 'muted' | 'brass' | 'brassBright' | 'win' | 'loss'
export type TextSize = 'sm' | 'md' | 'lg' | 'xl' | 'hero'

export interface TextProps extends HTMLAttributes<HTMLElement> {
  tone?: TextTone
  size?: TextSize
  /* Tabular figures for numbers that sit in columns. */
  mono?: boolean
  bold?: boolean
  upper?: boolean
  align?: 'left' | 'center' | 'right'
  as?: 'p' | 'span' | 'div'
  children?: ReactNode
}

const toneClass: Record<TextTone, string | undefined> = {
  default: undefined,
  muted: styles.toneMuted,
  brass: styles.toneBrass,
  brassBright: styles.toneBrassBright,
  win: styles.toneWin,
  loss: styles.toneLoss,
}

const sizeClass: Record<TextSize, string> = {
  sm: styles.sizeSm, md: styles.sizeMd, lg: styles.sizeLg, xl: styles.sizeXl, hero: styles.sizeHero,
}

export function textClass({ tone = 'default', size = 'md', mono, bold, upper, align = 'left', className }: Omit<TextProps, 'children' | 'as'>) {
  return [
    styles.text,
    sizeClass[size],
    toneClass[tone],
    mono && styles.mono,
    bold && styles.bold,
    upper && styles.upper,
    align === 'center' && styles.alignCenter,
    align === 'right' && styles.alignRight,
    className,
  ].filter(Boolean).join(' ')
}

export function Text({ tone, size, mono, bold, upper, align, as: Tag = 'p', className, children, ...rest }: TextProps) {
  return <Tag className={textClass({ tone, size, mono, bold, upper, align, className })} {...rest}>{children}</Tag>
}

/* Inline number: tabular figures so columns line up. */
export function Num({ children, tone, bold, size, className }: Pick<TextProps, 'children' | 'tone' | 'bold' | 'size' | 'className'>) {
  return <span className={textClass({ tone, bold, size, mono: true, className })}>{children}</span>
}

export interface TextLinkProps {
  href: string
  external?: boolean
  target?: string
  rel?: string
  children: ReactNode
}

/* Inline text link; SPA visit unless external. */
export function TextLink({ href, external, target, rel, children }: TextLinkProps) {
  return external || target
    ? <a href={href} target={target} rel={rel} className={styles.link}>{children}</a>
    : <Link href={href} className={styles.link}>{children}</Link>
}
